/**
 * @type {String|Null}
 * @description This will constain the base path for the worker javascript files
 * locations
 */
let _basePath = null

/**
 * @type {Function}
 * @param {String} path - The file path for the worker js files
 * @returns {String}
 */
export function setBasePath(path) {
  if (typeof path === 'string' && path.length > 0) {
    _basePath = path
  } else {
    throw new Error('Worker base path must be a string')
  }
  return _basePath
}

/**
 *
 * @param {String} name - the name of the worker JS file that we want to use to
 * create a new webworker. the name is all we need to pass, excluding the file
 * extension.
 * @returns {Object}
 * @description Creates a new web worker based on the file name given, this will try to
 * create the we worker using the _basePath as the location to load the worker files
 * from, if the worker is successfully created the return value will be an API to
 * manager the worker itself.
 */
export default function createWorker(name) {
  if (typeof name !== 'string') throw new Error('Worker name must be a string')
  if (name.indexOf('.') !== -1)
    throw new Error('Worker name must not be a file path')

  let _worker = null
  let _running = false

  /**
   * @type {Function}
   * @param {String} name
   * @returns {String}
   * @description Returns the full path for the worker file
   */
  const getPath = () => `${_basePath}/${name}.js`

  /**
   * @type {Function}
   * @description Tries to start the webworker, will set the _running flag
   * accordingly
   */
  const start = () => {
    if (_running) throw new Error(`${name} worker is already running`)
    try {
      _worker = new Worker(getPath())
      _running = true
    } catch (ex) {
      console.error(ex)
    }
  }

  /**
   * @type {Function}
   * @description Stops the webworker
   */
  const stop = () => {
    if (!_running) throw new Error('Worker is not running')
    try {
      _worker.terminate()
      _worker = null
      _running = false
    } catch (ex) {
      console.error(ex)
    }
  }

  /**
   * @type {Function}
   * @param {String} evt - the event to listen for
   * @param {Function} fn - The callback for the event handler
   * @description Sets up event handlers for the given event name
   */
  const on = (evt, fn) => {
    if (typeof evt !== 'string') throw new Error('Event name must be a string')
    if (typeof fn !== 'function') throw new Error('fn must be a Function')
    _worker.addEventListener(evt, createCallback(fn))
  }

  /**
   * @description Sends a message to the web worker.
   *
   * note: Using arrow functions here will not pass the arguments
   * into the worker correctly.
   */
  function send() {
    try {
      const args = Array.prototype.slice.call(arguments)
      _worker.postMessage(args)
    } catch (ex) {
      console.error('Unable to post to worker: ', ex)
    }
  }

  /**
   * @type {Function}
   * @param {Function} fn
   * @returns {Function}
   * @dscription Creates and returns a wrapped callback method, this
   * wrapps the callback in a try/catch statement to try to help prevent
   * errors.
   */
  const createCallback = (fn) => {
    return function execCallback() {
      try {
        const args = Array.prototype.slice.call(arguments)
        fn.apply(fn, args)
      } catch (ex) {
        console.error(ex)
      }
    }
  }

  return {
    start,
    stop,
    on,
    send,
  }
}
